import { Button } from "@/components/ui/button";
import { useCartStore } from "@/store/cartStore";
import { useWishlistStore } from "@/store/wishlistStore";
import { alertToast } from "@/contexts/AlertToastContext";
import { Link } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { useGlobalPreload } from "@/hooks/useGlobalPreload";
import { useIsMobile } from "@/hooks/use-mobile";
import { Loader2, Heart, Sparkles } from "lucide-react";
import type { ShopifyProduct } from "@/lib/shopify";
import { motion, AnimatePresence } from "motion/react";
import { formatPrice } from "@/lib/utils";
import { getColorHex, getColorHexSync, preloadColors } from "@/lib/colorService";
import { getJewelryGradient } from "@/lib/colorGradient";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { VariantQuickPicker } from "@/components/product/VariantQuickPicker";
import { getEngravingCount } from "@/components/cart/EngravingOptions";
import { hasIconTag } from "@/components/product/IconSelection";
import { TryOnDialog } from "@/components/product/TryOnDialog";

interface ShopifyProductCardProps {
  product: ShopifyProduct;
  collectionHandle?: string;
}

const ShopifyProductCard = ({ product, collectionHandle }: ShopifyProductCardProps) => {
  const addItem = useCartStore((state) => state.addItem);
  const isMobile = useIsMobile();
  const { toggleItem, isInWishlist } = useWishlistStore();
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showHeartParticles, setShowHeartParticles] = useState(false);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [quickPickerOpen, setQuickPickerOpen] = useState(false);
  const [colorHexValues, setColorHexValues] = useState<Record<string, string>>({});
  const [isLoadingColors, setIsLoadingColors] = useState(false);
  const [selectedColorValue, setSelectedColorValue] = useState<string | null>(null);
  const [tryOnOpen, setTryOnOpen] = useState(false);
  const virtualTryOnEnabled = useGlobalPreload((s) => s.siteSettings?.virtual_tryon_enabled !== false);
  
  const isLiked = isInWishlist(product.id);

  const isColorOptionName = (name: string) => {
    const n = name.toLowerCase();
    return (
      n === 'צבע' ||
      n === 'color' ||
      n === 'colour' ||
      n.includes('צבע') ||
      n.includes('color') ||
      n.includes('colour') ||
      n === 'גוון' ||
      n === 'shade' ||
      n === 'metal' ||
      n === 'מתכת'
    );
  };

  // Get color option from product - check multiple possible names
  const colorOption = product.options?.find((option) => isColorOptionName(option.name));

  // Find variant by selected color
  const getVariantByColor = (colorValue: string) => {
    return product.variants?.edges?.find((edge) =>
      edge.node.selectedOptions?.some((opt) => isColorOptionName(opt.name) && opt.value === colorValue)
    )?.node;
  };

  // Get the selected variant or default to first
  const selectedVariant = selectedColorValue 
    ? getVariantByColor(selectedColorValue) 
    : product.variants?.edges?.[0]?.node;

  // Load color hex values for color options using batch service
  useEffect(() => {
    const loadColorValues = async () => {
      if (!colorOption) return;
      
      const colorsToLoad: string[] = [];
      const immediateValues: Record<string, string> = {};
      
      for (const value of colorOption.values) {
        const cached = getColorHexSync(value);
        if (cached) {
          immediateValues[value] = cached;
        } else {
          colorsToLoad.push(value);
        }
      }
      
      // Set immediate cached values
      if (Object.keys(immediateValues).length > 0) {
        setColorHexValues(prev => ({ ...prev, ...immediateValues }));
      }
      
      // Load uncached colors
      if (colorsToLoad.length > 0) {
        setIsLoadingColors(true);
        
        // Preload to trigger batch
        preloadColors(colorsToLoad);
        
        // Get all colors
        const newValues: Record<string, string> = {};
        await Promise.all(
          colorsToLoad.map(async (value) => {
            const hex = await getColorHex(value);
            newValues[value] = hex;
          })
        );
        
        setColorHexValues(prev => ({ ...prev, ...newValues }));
        setIsLoadingColors(false);
      }
    };
    
    loadColorValues();
  }, [colorOption]);

  const handleColorSelect = (e: React.MouseEvent, colorValue: string) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedColorValue(colorValue === selectedColorValue ? null : colorValue);
  };

  // Smart logic: can we add directly without opening the picker?
  const canAddDirectly = useCallback(() => {
    // If product has engraving or icons, always open picker
    if (getEngravingCount(product.tags) > 0) return false;
    if (hasIconTag(product.tags)) return false;

    // Check if there are complex options (not color, not Title, with multiple values)
    const complexOptions = (product.options || []).filter(
      (opt) => !isColorOptionName(opt.name) && opt.name !== "Title" && opt.values.length > 1
    );
    if (complexOptions.length > 0) return false;

    // If there's a color option, user must have selected one
    if (colorOption) {
      return selectedColorValue !== null;
    }

    // No complex options, no color - can add directly
    return true;
  }, [product, colorOption, selectedColorValue]);

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!selectedVariant || !selectedVariant.availableForSale) {
      alertToast.error('המוצר אינו זמין כרגע');
      return;
    }

    if (!canAddDirectly()) {
      setQuickPickerOpen(true);
      return;
    }

    setIsAddingToCart(true);
    await new Promise(resolve => setTimeout(resolve, 300));

    addItem({
      product: product,
      variantId: selectedVariant.id,
      variantTitle: selectedVariant.title,
      price: {
        amount: selectedVariant.price.amount,
        currencyCode: selectedVariant.price.currencyCode,
      },
      quantity: 1,
      variantImage: selectedVariant.image ? {
        url: selectedVariant.image.url,
        altText: selectedVariant.image.altText || undefined,
      } : undefined,
    });

    if (isLiked) {
      toggleItem(product);
    }

    alertToast.success('המוצר נוסף לסל!');
    setIsAddingToCart(false);
  };

  const handleToggleWishlist = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const wasLiked = isLiked;
    const result = await toggleItem(product);
    
    if (result && !wasLiked) {
      setShowHeartParticles(true);
      setTimeout(() => setShowHeartParticles(false), 1000);
      alertToast.success('המוצר נוסף למועדפים!');
    } else if (wasLiked) {
      alertToast.info('המוצר הוסר מהמועדפים');
    }
  };

  // Image: prefer selected variant image (if exists), fallback to product main image
  const availableImages = product.images?.edges || [];
  const baseImage = availableImages[0]?.node;
  const imageUrl = selectedVariant?.image?.url ?? baseImage?.url;
  const imageAlt = selectedVariant?.image?.altText ?? baseImage?.altText ?? product.title;
  const price = product.priceRange.minVariantPrice;

  // Reset loading state when image changes (for smooth crossfade + correct loader)
  useEffect(() => {
    setImageLoaded(false);
  }, [imageUrl]);

  const [isCardHovered, setIsCardHovered] = useState(false);

  // Build URL with collection context and color parameter if selected
  // Shopify-compatible URL: /collections/[collection]/products/[handle]?variant=xxx
  const getProductUrl = () => {
    const basePath = collectionHandle 
      ? `/collections/${collectionHandle}/products/${product.handle}`
      : `/products/${product.handle}`;
    
    const params = new URLSearchParams();
    if (selectedColorValue) {
      params.set('color', selectedColorValue);
    }
    if (selectedVariant?.id) {
      // Extract variant ID number from GraphQL ID
      const variantIdMatch = selectedVariant.id.match(/ProductVariant\/(\d+)/);
      if (variantIdMatch) {
        params.set('variant', variantIdMatch[1]);
      }
    }
    
    const queryString = params.toString();
    return queryString ? `${basePath}?${queryString}` : basePath;
  };

  const productUrl = getProductUrl();

  return (
    <>
    <Link 
      to={productUrl}
      className="group cursor-pointer block h-full"
      onMouseEnter={() => setIsCardHovered(true)}
      onMouseLeave={() => setIsCardHovered(false)}
    >
        <div className="border border-foreground/20 transition-all duration-300 hover:shadow-sm bg-background flex flex-col h-[400px] sm:h-[420px] md:h-[440px] relative overflow-hidden">
          {/* Preview + Try On Overlay - Shows on card hover */}
          <div className={`absolute inset-0 bg-foreground/5 z-20 flex items-center justify-center transition-all duration-300 pointer-events-none ${isCardHovered ? 'opacity-100' : 'opacity-0'}`}>
            <div className={`flex flex-col gap-2.5 transition-all duration-500 ${isCardHovered ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0'}`}>
              <div className="bg-background/95 backdrop-blur-sm px-5 py-2.5 border border-foreground/10 shadow-lg flex items-center justify-center">
                <span className="text-[13px] text-foreground/80" style={{ fontWeight: 400 }}>פתח תצוגה מקדימה</span>
              </div>
            </div>
          </div>

          {/* Product Image */}
          <div className="relative w-full overflow-hidden p-3 sm:p-4 pb-0 flex-shrink-0 h-[240px] sm:h-[260px] md:h-[280px]">
          {/* Wishlist Heart Button */}
          <motion.button
            onClick={handleToggleWishlist}
            className="absolute top-3 left-3 z-30 bg-background/80 hover:bg-background p-2 rounded-full transition-all duration-300"
            whileTap={{ scale: 0.8 }}
            animate={isLiked ? { scale: [1, 1.2, 1] } : {}}
            transition={{ duration: 0.3 }}
            aria-label={isLiked ? 'הסר מהמועדפים' : 'הוסף למועדפים'}
          >
            <motion.div
              animate={isLiked ? { 
                scale: [1, 1.2, 1],
                rotate: [0, -10, 10, -10, 0]
              } : {}}
              transition={{ duration: 0.5 }}
            >
              <Heart 
                className={`h-4 w-4 transition-all duration-300 ${
                  isLiked 
                    ? 'fill-red-500 text-red-500' 
                    : 'text-foreground/60 hover:text-red-500'
                }`}
              />
            </motion.div>
          </motion.button>

          {/* Heart Particles Animation */}
          <AnimatePresence>
            {showHeartParticles && (
              <>
                {[...Array(6)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute top-3 left-3 z-10 pointer-events-none"
                    initial={{ 
                      scale: 0.5,
                      opacity: 1,
                      x: 0,
                      y: 0
                    }}
                    animate={{
                      scale: 0,
                      opacity: 0,
                      x: Math.cos((i * Math.PI * 2) / 6) * 50,
                      y: Math.sin((i * Math.PI * 2) / 6) * 50,
                    }}
                    exit={{ opacity: 0 }}
                    transition={{
                      duration: 0.8,
                      ease: "easeOut"
                    }}
                  >
                    <Heart className="h-3 w-3 fill-red-500 text-red-500" />
                  </motion.div>
                ))}
              </>
            )}
          </AnimatePresence>
          
          {imageUrl ? (
            <AnimatePresence mode="wait">
              <motion.div
                key={imageUrl}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="w-full h-full"
              >
                {!imageLoaded && (
                  <div className="absolute inset-0 m-6 mb-0 flex flex-col items-center justify-center bg-gradient-to-r from-muted/30 to-muted/10 gap-2">
                    <div className="gold-bar-loader" style={{ width: '80px' }} />
                  </div>
                )}
                <img
                  src={imageUrl}
                  alt={imageAlt}
                  loading="lazy"
                  className={`w-full h-full object-contain group-hover:scale-105 transition-transform duration-700 ease-out ${
                    imageLoaded ? 'opacity-100' : 'opacity-0'
                  }`}
                  onLoad={() => setImageLoaded(true)}
                />
              </motion.div>
            </AnimatePresence>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-r from-muted/30 to-muted/10">
              <span className="text-muted-foreground/50 text-sm" style={{ fontWeight: 300 }}>אין תמונה</span>
            </div>
          )}
        </div>

        {/* Product Title with Description Tooltip */}
        <div className="px-4 pt-2 flex-shrink-0">
          <TooltipProvider delayDuration={4000}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="h-[40px] bg-gradient-to-r from-muted/30 to-muted/10 flex items-center rtl:justify-end ltr:justify-start px-2 py-1 cursor-default">
                  <h3 className="text-sm sm:text-base text-foreground rtl:text-right ltr:text-left line-clamp-2" style={{ fontWeight: 400 }}>
                    {product.title}
                  </h3>
                </div>
              </TooltipTrigger>
              {product.description && product.description.trim().length > 0 && (
                <TooltipContent 
                  side="top" 
                  className="bg-foreground text-background text-xs px-3 py-2 max-w-[320px] max-h-[300px] overflow-y-auto text-right leading-relaxed"
                >
                  {product.description}
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Divider */}
        <div className="px-4 flex-shrink-0" style={{ paddingTop: '8px', paddingBottom: '8px' }}>
          <div className="w-full" style={{ height: '1px', backgroundColor: '#d4d4d4' }}></div>
        </div>

        {/* Price and Color Swatches Row */}
        <div className="px-4 pt-1 flex-shrink-0 flex items-center justify-between">
          {/* Price - Right side */}
          <p className="text-primary text-base" style={{ fontWeight: 400 }}>
            {formatPrice(selectedVariant?.price?.amount || price.amount, selectedVariant?.price?.currencyCode || price.currencyCode)}
          </p>
          
          {/* Color Swatches - Left side */}
          {colorOption && colorOption.values.length > 0 && (
            <TooltipProvider delayDuration={100}>
              <div className="flex items-center gap-1.5 flex-wrap">
                {colorOption.values.slice(0, 5).map((colorValue) => {
                  const hexColor = colorHexValues[colorValue];
                  const isWhiteColor = hexColor?.toUpperCase() === '#FFFFFF' || 
                                      hexColor?.toUpperCase() === '#E8E8E8' ||
                                      hexColor?.toUpperCase() === '#E5E4E2';
                  const isSelected = selectedColorValue === colorValue;
                  
                    return (
                      <Tooltip key={colorValue}>
                        <TooltipTrigger asChild>
                          <button
                            onClick={(e) => handleColorSelect(e, colorValue)}
                            className={`w-6 h-6 rounded-full transition-all duration-200 hover:scale-110 relative overflow-hidden ${
                              isWhiteColor ? 'border border-border' : ''
                            } ${
                              isSelected ? 'ring-2 ring-primary ring-offset-1 scale-110' : ''
                            }`}
                            style={{ 
                              background: isLoadingColors && !hexColor 
                                ? '#e5e5e5' 
                                : hexColor 
                                  ? getJewelryGradient(hexColor) 
                                  : getJewelryGradient('#D4AF37'),
                            }}
                            aria-label={colorValue}
                          >
                            {isLoadingColors && !hexColor && (
                              <span className="block w-full h-full rounded-full animate-pulse bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200" />
                            )}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent 
                          side="top" 
                          className="bg-foreground text-background text-xs px-2 py-1"
                        >
                          {colorValue}
                        </TooltipContent>
                      </Tooltip>
                    );
                })}
                {colorOption.values.length > 5 && (
                  <span className="text-xs text-muted-foreground">+{colorOption.values.length - 5}</span>
                )}
              </div>
            </TooltipProvider>
          )}
        </div>

        {/* Add to Cart Button with Hover Animation */}
        <div className="px-4 pb-4 pt-3 mt-auto flex-shrink-0 relative z-30 flex">
          {/* Add to Cart Button */}
          <button
            onClick={handleAddToCart}
            onMouseEnter={(e) => { e.stopPropagation(); setIsButtonHovered(true); }}
            onMouseLeave={(e) => { e.stopPropagation(); setIsButtonHovered(false); }}
            disabled={isAddingToCart}
            className="relative h-[35px] overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed flex-1 min-w-0"
            style={{ 
              backgroundColor: '#7e6b44',
              fontFamily: "'Almoni DL', system-ui, sans-serif",
              fontSize: '14px'
            }}
          >
            {/* Tooltip */}
            <div className={`absolute bottom-[calc(100%+28px)] left-1/2 -translate-x-1/2 w-[90px] h-[35px] bg-[#7e6b44] text-white text-[0.9rem] rounded flex items-center justify-center transition-all duration-500 ${isButtonHovered ? 'opacity-100 visible bottom-[calc(100%+18px)]' : 'opacity-0 invisible'}`}>
              {formatPrice(price.amount, price.currencyCode)}
            </div>
            {/* Tooltip Arrow */}
            <div className={`absolute bottom-[calc(100%+8px)] left-1/2 -translate-x-1/2 w-0 h-0 border-[10px] border-transparent border-t-[#7e6b44] transition-all duration-500 ${isButtonHovered ? 'opacity-100 visible bottom-[calc(100%+18px)]' : 'opacity-0 invisible'}`} />
            {/* Button Content */}
            <div className="relative w-full h-full overflow-hidden">
              <div 
                className="absolute inset-0 flex items-center justify-center text-white transition-transform duration-500"
                style={{ transform: isButtonHovered ? 'translateY(-100%)' : 'translateY(0)' }}
              >
                {isAddingToCart ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'הוספה לסל'
                )}
              </div>
              <div 
                className="absolute inset-0 flex items-center justify-center text-white transition-transform duration-500"
                style={{ transform: isButtonHovered ? 'translateY(0)' : 'translateY(100%)' }}
              >
                <svg viewBox="0 0 16 16" className="w-5 h-5" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 2.5A.5.5 0 0 1 .5 2H2a.5.5 0 0 1 .485.379L2.89 4H14.5a.5.5 0 0 1 .485.621l-1.5 6A.5.5 0 0 1 13 11H4a.5.5 0 0 1-.485-.379L1.61 3H.5a.5.5 0 0 1-.5-.5zM3.14 5l1.25 5h8.22l1.25-5H3.14zM5 13a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm-2 1a2 2 0 1 1 4 0 2 2 0 0 1-4 0zm9-1a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm-2 1a2 2 0 1 1 4 0 2 2 0 0 1-4 0z" />
                </svg>
              </div>
            </div>
          </button>

          {/* AI Try-On Button - always visible on mobile, slides in on hover for desktop */}
          {virtualTryOnEnabled && imageUrl && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setTryOnOpen(true); }}
              className="h-[35px] flex items-center justify-center border border-primary/30 bg-background hover:bg-primary/5 transition-all duration-500 ease-in-out overflow-hidden flex-shrink-0"
              aria-label="נסה עליך"
              style={{ 
                width: isMobile || isCardHovered ? '35px' : '0px',
                opacity: isMobile || isCardHovered ? 1 : 0,
                borderWidth: isMobile || isCardHovered ? undefined : '0px',
                padding: isMobile || isCardHovered ? undefined : '0px',
                marginInlineStart: isMobile || isCardHovered ? '6px' : '0px',
              }}
            >
              <Sparkles className="h-4 w-4 text-primary flex-shrink-0 animate-[pulse_2s_ease-in-out_infinite]" />
            </button>
          )}
        </div>
        </div>
    </Link>
    
    {/* Variant Quick Picker - outside Link to prevent navigation */}
    <VariantQuickPicker
      product={product}
      open={quickPickerOpen}
      onClose={() => setQuickPickerOpen(false)}
      defaultOptions={selectedColorValue && colorOption ? { [colorOption.name]: selectedColorValue } : undefined}
    />
    
    {/* Virtual Try-On Dialog */}
    {virtualTryOnEnabled && imageUrl && (
      <TryOnDialog
        open={tryOnOpen}
        onOpenChange={setTryOnOpen}
        productTitle={product.title}
        productImage={imageUrl}
        product={product}
      />
    )}
    </>
  );
};

export default ShopifyProductCard;